import { forwardRef, useEffect, useRef, useState } from "react";
import type { ButtonHTMLAttributes } from "react";
import SVG from "react-inlinesvg";
import IconButton from "src/components/IconButton";
import useOnClickOutside from "src/hooks/useOnClickOutside";
import { cn } from "src/lib/cn";
import { composerChromeButtonClassName } from "src/lib/composerChrome";
import GenericPopover from "src/sudokit/components/Popover/GenericPopover";
import { Spinner } from "src/sudokit/components/Spinner";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { Text } from "src/sudokit/components/Text";
import StyleThumbnail from "./StyleThumbnail";
import type { StylePresetOption } from "./lib/types";

const MAX_SELECTED_STYLES = 4;
// Past this many presets the one-per-row list gets taller than the popover can show, so it switches to a two-column grid.
const COMPACT_LAYOUT_MIN_PRESETS = 7;

interface StylesPopoverProps {
  /** null while the preset list is still loading. */
  stylePresets: StylePresetOption[] | null;
  stylePresetsError: string | null;
  onRetry: () => void;
  selectedStyleIds: string[];
  onToggleStyle: (styleId: string) => void;
  disabled?: boolean;
  width?: string;
}

const StylesTrigger = forwardRef<HTMLButtonElement, Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children">>(({ className, ...rest }, ref) => (
  <button
    ref={ref}
    {...rest}
    type="button"
    // Borderless in the generator's composer, unlike chat's outlined chips.
    className={cn(composerChromeButtonClassName, "border-transparent px-2", className)}
  >
    <SVG
      src="/assets/add.svg"
      width={16}
      height={16}
      className="shrink-0 [&_path]:fill-current"
      aria-hidden="true"
    />
    <span>Style</span>
  </button>
));
StylesTrigger.displayName = "StylesTrigger";

function StyleRow({ id, label, description, checked, disabled, compact, onToggle }: { id: string; label: string; description: string; checked: boolean; disabled?: boolean; compact?: boolean; onToggle: () => void }) {
  const iconSize = compact ? 20 : 24;
  return (
    <div
      // Compact rows drop the description line, so it's exposed as a tooltip instead.
      title={compact ? description : undefined}
      // Mouse-only convenience: the inner button is the single keyboard/AT control, so the row
      // itself must not be focusable or expose a role.
      onClick={() => {
        if (!disabled) onToggle();
      }}
      // border-solid is required explicitly: Tailwind's preflight reset is disabled app-wide.
      className={cn(
        "flex items-center border-b border-solid border-app-grey-3",
        compact ? "min-w-0 gap-2 border-r px-3 py-2 [&:nth-child(2n)]:border-r-0" : "gap-3 px-4 py-3 last:border-b-0",
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
      )}
    >
      <StyleThumbnail
        styleId={id}
        size={compact ? 32 : 72}
      />
      <div className="flex min-w-0 flex-1 flex-col select-none">
        <Text
          size={compact ? "sm" : "base"}
          weight="medium"
          truncate={compact}
        >
          {label}
        </Text>
        {!compact && (
          <Text
            size="sm"
            color="grey-1"
            truncate
          >
            {description}
          </Text>
        )}
      </div>
      <button
        type="button"
        aria-label={checked ? `Deselect ${label}` : `Select ${label}`}
        aria-pressed={checked}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) onToggle();
        }}
        className={cn("flex shrink-0 appearance-none items-center justify-center border-0 bg-transparent p-0", compact ? "h-5 w-5" : "h-6 w-6")}
      >
        <SVG
          src={checked ? "/assets/check-selected.svg" : "/assets/radio-unselected.svg"}
          width={iconSize}
          height={iconSize}
          className={checked ? "[&_path]:fill-app-primary-cta-high-contrast" : undefined}
        />
      </button>
    </div>
  );
}

export default function StylesPopover({ stylePresets, stylePresetsError, onRetry, selectedStyleIds, onToggleStyle, disabled, width = "320px" }: StylesPopoverProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const atCap = selectedStyleIds.length >= MAX_SELECTED_STYLES;
  const compact = (stylePresets?.length ?? 0) >= COMPACT_LAYOUT_MIN_PRESETS;

  // GenericPopover's own onInteractOutside no-ops in non-modal mode, so outside-click close is
  // handled here instead, checking both the trigger and the portaled content.
  useOnClickOutside([triggerRef, contentRef], () => {
    if (open) setOpen(false);
  });

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  return (
    <GenericPopover
      open={open}
      onOpenChange={setOpen}
      modal={false}
      side="top"
      align="start"
      width={width}
      // "auto" skips GenericPopover's ScrollArea wrapper, which otherwise overflows this fixed list.
      maxHeight="auto"
      // Above the sudokit Dialog's z-[999999999] this popover opens inside of.
      className="z-[1000000000]"
      trigger={
        <StylesTrigger
          ref={triggerRef}
          disabled={disabled}
        />
      }
    >
      <div
        ref={contentRef}
        className="flex flex-col gap-4 p-4"
      >
        <div className="flex items-center gap-2">
          <Text
            size="sm"
            weight="semibold"
            className="flex-1 uppercase tracking-wide"
          >
            Styles
          </Text>
          <IconButton
            src="/assets/close.svg"
            ariaLabel="Close"
            size={28}
            iconSize={14}
            onClick={() => setOpen(false)}
          />
        </div>
        <div className={cn("overflow-hidden rounded-xl border border-solid border-app-grey-3", compact && "grid grid-cols-2")}>
          {stylePresets === null && stylePresetsError ? (
            <div className="flex flex-col items-center gap-3 px-4 py-6 text-center">
              <Text
                size="sm"
                color="grey-1"
              >
                {stylePresetsError}
              </Text>
              <SudoButton
                variant="secondary"
                size="small"
                onClick={onRetry}
              >
                Try again
              </SudoButton>
            </div>
          ) : stylePresets === null ? (
            <div className="flex items-center justify-center py-8">
              <Spinner
                size="md"
                label="Loading styles"
              />
            </div>
          ) : (
            stylePresets.map((preset) => {
              const checked = selectedStyleIds.includes(preset.id);
              const disabled = !checked && atCap;
              return (
                <StyleRow
                  key={preset.id}
                  id={preset.id}
                  label={preset.label}
                  description={preset.description}
                  checked={checked}
                  disabled={disabled}
                  compact={compact}
                  onToggle={() => onToggleStyle(preset.id)}
                />
              );
            })
          )}
        </div>
        <Text
          size="sm"
          color="grey-1"
        >
          Select up to 4
        </Text>
      </div>
    </GenericPopover>
  );
}
